import { Box, Button, Chip, Container, Grid, IconButton } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Iconify from "src/components/Iconify";
import Page from "src/components/Page";
import MRTTable from "src/components/mrt-table";
import useSettings from "src/hooks/useSettings";
import { getAllDocumentTypes } from "src/pages/api";
import AddDocumentType from "./AddDocumentType";
import DeleteRecord from "src/components/DeleteRecord";

const columns = [
  { accessorKey: "name", header: "Document Type", size: 300 },
  {
    accessorKey: "isActive",
    header: "Status",
    size: 120,
    Cell: ({ cell }) =>
      cell.getValue() ? (
        <Chip label="Active" color="success" size="small" variant="outlined" />
      ) : (
        <Chip label="Inactive" color="default" size="small" variant="outlined" />
      ),
  },
];

export default function DocumentTypeMaster() {
  const { themeStretch } = useSettings();

  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["document-types", ""],
    queryFn: getAllDocumentTypes,
    keepPreviousData: true,
    staleTime: 0,
  });

  const filteredRows = useMemo(
    () =>
      (data?.data || []).filter((row) =>
        globalFilter ? row.name?.toLowerCase().includes(globalFilter.toLowerCase()) : true
      ),
    [data, globalFilter]
  );

  const handleRowClick = (row) => {
    setOpen(true);
    setSelectedRow(row.original);
    setIsEditMode(true);
  };

  return (
    <>
      {open && (
        <AddDocumentType
          onClose={() => {
            setOpen(false);
            setSelectedRow(null);
            setIsEditMode(false);
            refetch();
          }}
          row={selectedRow}
          isEditMode={isEditMode}
        />
      )}
      {openDelete && selectedRow && (
        <DeleteRecord
          row={selectedRow}
          title="Delete Document Type"
          endpoint="/documents/document-types"
          labelKey="name"
          queryKey={["document-types"]}
          onClose={() => {
            setOpenDelete(false);
            setSelectedRow(null);
            refetch();
          }}
        />
      )}
      <Page title="Document Types">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Document Types"
            links={[{ name: "Settings" }, { name: "Document Types" }]}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ overflowX: "auto", minWidth: 0 }}>
              <MRTTable
                data={filteredRows}
                columns={columns}
                loading={isLoading || isFetching}
                pagination={pagination}
                onPaginationChange={setPagination}
                rowCount={filteredRows.length}
                onGlobalFilterChange={(value) => {
                  setGlobalFilter(value ?? "");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRowClick(row),
                  sx: {
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  },
                })}
                renderRowActions={({ row }) => (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDelete(true);
                        setSelectedRow(row.original);
                      }}
                      color="error"
                      size="small"
                    >
                      <Iconify icon={"eva:trash-2-outline"} />
                    </IconButton>
                  </Box>
                )}
                renderTopToolbarCustomActions={() => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setOpen(true);
                        setIsEditMode(false);
                      }}
                      sx={{ minWidth: "120px", fontSize: "0.875rem", py: 1 }}
                    >
                      Add New
                    </Button>
                  </Box>
                )}
                enableToolbarInternalActions={false}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableSorting={true}
                enableGlobalFilter={true}
                enableHiding={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enablePagination={true}
                positionPagination="bottom"
                enableColumnDragging={false}
                positionGlobalFilter="left"
                muiSearchTextFieldProps={{
                  placeholder: "Search",
                  size: "small",
                  variant: "outlined",
                  value: globalFilter,
                  onChange: (e) => setGlobalFilter(e.target.value),
                }}
                initialState={{
                  showGlobalFilter: true,
                  density: "comfortable",
                  pagination: { pageSize: 10, pageIndex: 0 },
                }}
                state={{
                  globalFilter,
                  pagination,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
